<template>
  <span :class="className" role="alert" aria-live="assertive">
    <button
      type="button"
      class="text-to-speech-button"
      tabindex="0"
      @click="onButtonClick"
      :title="buttonTitle"
    >
      <FaIcon
        :icon="hasError ? 'volume-slash' : 'volume'"
        type="solid"
        class="text-to-speech-icon"
      />
      <svg
        class="text-to-speech-status"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <circle
          cx="50"
          cy="50"
          r="45"
          transform="rotate(-90 50 50)"
          :stroke-dasharray="2 * 50 * Math.PI"
          :stroke-dashoffset="(1 - currentTime / duration) * 2 * 50 * Math.PI"
        ></circle>
      </svg>
      <svg
        class="text-to-speech-loading"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        xmlns:xlink="http://www.w3.org/1999/xlink"
      >
        <circle
          cx="50"
          cy="50"
          r="25"
          :stroke-dasharray="(2 * 25 * Math.PI) / 3"
          :stroke-dashoffset="2 * 25 * Math.PI"
        ></circle>
      </svg>
    </button>
    <audio
      preload="none"
      :src="audioUrl"
      ref="audioPlayer"
      @load="onLoad"
      @error="onError"
      @playing="onPlaying"
      @pause="onPause"
      @ended="onEnd"
      @timeupdate="updateTimeStatus"
      @durationchange="updateTimeStatus"
    ></audio>
  </span>
</template>

<script>
import sha1 from 'js-sha1'

import FaIcon from '../ui/FaIcon'

export default {
  name: 'TextToSpeech',
  components: { FaIcon },
  props: {
    text: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isPlaying: false,
      isLoading: false,
      hasError: false,
      duration: 0.1,
      currentTime: 0,
    }
  },
  computed: {
    buttonTitle() {
      if (this.hasError) {
        return this.$t('components.text-to-speech.retry-button')
      }

      if (this.isPlaying) {
        return this.$t('components.text-to-speech.stop-button')
      }

      return this.$t('components.text-to-speech.play-button')
    },
    className() {
      const classNames = ['text-to-speech']

      if (this.hasError) {
        classNames.push('error')
      } else if (this.isLoading) {
        classNames.push('is-loading')
      } else if (this.isPlaying) {
        classNames.push('is-playing')
      }

      return classNames.join(' ')
    },
    /**
     * The URL for the audio file
     * @returns {string}
     */
    audioUrl() {
      const subDir = this.fileName.substring(0, 2)

      return `${this.$config.dynCdnUrl}/sound/dynamic/tts/${subDir}/${this.fileName}.mp3?text=${this.finalUriEncodedText}`
    },

    /**
     * The first layer of uri encoding of the text string
     * @returns {string}
     */
    internalUriEncodedText() {
      return encodeURIComponent(this.text)
    },

    /**
     * A base 64 encoded version of the uri encoded text.
     * @returns {string}
     */
    base64EncodedText() {
      return btoa(this.internalUriEncodedText)
    },

    /**
     * The file name to use on the dynamic CDN server.
     * Consists of a sha1 hex of the base 64 encoded string.
     * @returns {string}
     */
    fileName() {
      return sha1.hex(this.base64EncodedText)
    },

    /**
     * An URI encoded version of the base 64 encoded string.
     * @returns {string}
     */
    finalUriEncodedText() {
      return encodeURIComponent(this.base64EncodedText)
    },
  },
  methods: {
    onButtonClick(event) {
      // Prevents links from being followed when the Text To Speech player is placed inside of a link
      event.preventDefault()

      if (this.hasError) {
        this.hasError = false
        this.isLoading = true

        this.$refs.audioPlayer.load()
        this.$refs.audioPlayer.play()
      } else if (this.isLoading) {
        this.$refs.audioPlayer.pause()
      } else if (this.isPlaying) {
        this.$refs.audioPlayer.pause()
        this.$refs.audioPlayer.currentTime = 0
      } else {
        this.isLoading = true

        this.$refs.audioPlayer.play()
      }
    },
    updateTimeStatus() {
      if (!this.$refs.audioPlayer) {
        return
      }

      this.duration = this.$refs.audioPlayer.duration || 0.1
      this.currentTime = this.$refs.audioPlayer.currentTime
      if (this.currentTime > 0) {
        this.currentTime = Math.min(this.currentTime + 0.15, this.duration)
      }
    },
    onLoad() {
      this.isLoading = false
    },
    onError() {
      this.isLoading = false
      this.hasError = true
    },
    onPlaying() {
      this.isPlaying = true
      this.isLoading = false
    },
    onPause() {
      this.isPlaying = false
    },
    onEnd() {
      this.currentTime = this.duration
      this.isPlaying = false

      setTimeout(() => {
        this.currentTime = 0
      }, 300)
    },
  },
}
</script>

<style lang="scss" scoped>
.text-to-speech {
  $size: 2em;

  position: relative;
  display: inline-block;
  width: $size;
  height: $size;
  vertical-align: middle;
  color: rgba(0, 0, 0, 0.25);
  transition: color 0.1s ease-in-out;

  &.error {
    color: rgba($red, 0.5);
  }

  &:focus-within {
    color: rgba(0, 0, 0, 0.75);

    &.error {
      color: rgba($red, 1);
    }
  }
}

.text-to-speech-button {
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
  background: transparent;

  // Override global Fessor style
  box-shadow: none !important;
  color: inherit !important;
}

.text-to-speech-icon,
.text-to-speech-status,
.text-to-speech-loading {
  position: absolute;
  display: block;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: opacity 0.3s ease-out;
}

.text-to-speech-icon {
  // half width of the overall TextToSpeech, to allow the progress circle to fill the outsides
  width: 1em;
  height: auto;
  color: inherit;
}

.text-to-speech-status,
.text-to-speech-loading {
  width: 100%;

  > circle {
    fill: transparent;
    stroke: currentColor;
    stroke-width: 5;
    stroke-linecap: round;
    transition: all 0.5s linear;
  }
}

@keyframes tts-playing {
  0%,
  100% {
    transform: translate(-50%, -55%) scale(0.9);
  }
  30% {
    transform: translate(-50%, -40%) scale(0.8) rotate(10deg);
  }
  50% {
    transform: translate(-50%, -55%) scale(0.9);
  }
  80% {
    transform: translate(-50%, -35%) scale(0.85) rotate(-13deg);
  }
}

@keyframes tts-loading {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.text-to-speech {
  &.is-playing {
    .text-to-speech-icon {
      animation: tts-playing 1.3s infinite ease-in-out;
    }
  }

  &:not(.is-playing) {
    .text-to-speech-status {
      opacity: 0;
    }
  }

  &:not(.is-loading) {
    .text-to-speech-loading {
      opacity: 0;
    }
  }

  &.is-loading {
    .text-to-speech-icon {
      opacity: 0;
    }
    .text-to-speech-loading {
      animation: tts-loading 1s infinite linear;
    }
  }
}
</style>
